import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import params from '../config/Params';
import { getItemFromLocalStorage, setWithExpiry } from '../utils/storageUtils';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import ImageCropper from '../components/ImageCropper';

function MyProfile() {
  const navigate = useNavigate();

  const [croppedImage, setCroppedImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imagePreview, setImagePreview] = useState('/assets/img/host_default_image.png');
  const [sourceId, setSourceId] = useState('');
  const [hostId, setHostId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [number, setNumber] = useState('');
  const [gstin, setGstin] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const successMessageRef = useRef(null);

  useEffect(() => {
    if (successMessage && successMessageRef.current) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [successMessage]);

  useEffect(() => {
    const profileData = getItemFromLocalStorage('hostProfile');
    if (profileData) {
      setFirstName(profileData.host_name || '');
      setMiddleName(profileData.host_midname || '');
      setLastName(profileData.host_lastname || '');
      setGstin(profileData.host_gst_no || '');
      setCompanyName(profileData.host_legal_name || '');
      setEmail(profileData.host_contact_email || '');
      setNumber(profileData.host_contact_number || '');
      setHostId(profileData.host_id || '');
      setSourceId(profileData.host_login_id || '');
      setBrandName(profileData.brand_name || '');
      setImagePreview(profileData.host_logo || '/assets/img/host_default_image.png');
    }
  }, []);

  const handleCrop = (croppedDataUrl) => {
    setCroppedImage(croppedDataUrl);
    console.log(croppedDataUrl);
    setShowCropper(false);
  };

  const base64ToBlob = (base64, mime) => {
    const byteString = atob(base64.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mime });
  };

  const handleGstinChange = async (e) => {
    const value = e.target.value;
    setGstin(value);

    if (value.length === 15) {
      let gstData = new FormData();
      gstData.append('gstin', value);
      try {
        const response = await axios({
          method: 'post',
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          url: `${params.baseURL}${params.endpoints.verifyGst}`,
          data: gstData
        });
  
        if (response.data.status === 'ok') {
          setCompanyName(response.data.gst_legal_name);
        } else if (response.data.status === 'error') {
          toast.error(response.data.list);
        } else {
          toast.error('An error occurred while saving data.');
        }
      } catch (err) {
        toast.error('An error occurred while saving data.');
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{10}$/;

    if (!firstName) errors.firstName = true;
    if (!lastName) errors.lastName = true;

    if (!email || !emailRegex.test(email)) {
      errors.email = true;
    }

    if (!number || !phoneRegex.test(number)) {
      errors.number = true;
    }

    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    switch (name) {
      case 'firstName':
        setFirstName(value);
        setFormErrors((prev) => ({ ...prev, firstName: !value }));
        break;

      case 'middleName':
        setMiddleName(value);
        break;

      case 'lastName':
        setLastName(value);
        setFormErrors((prev) => ({ ...prev, lastName: !value }));
        break;

      case 'email':
        setEmail(value);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        setFormErrors((prev) => ({
          ...prev,
          email: !emailRegex.test(value),
        }));
        break;

      case 'number':
        setNumber(value);
        const phoneRegex = /^\d{10}$/;
        setFormErrors((prev) => ({
          ...prev,
          number: !phoneRegex.test(value),
        }));
        break;

      case 'companyName':
        setCompanyName(value);
        break;

      case 'brandName':
        setBrandName(value);
        break;

      default:
        break;
    }
  };

  const ProceedRegister = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.error("Please correct the highlighted fields.");
      return;
    }

    const hostData = getItemFromLocalStorage('hostProfile');

    let formData = new FormData();
    formData.append('token', hostData.host_access_token);
    formData.append('id', hostId);
    formData.append('host_login_source', 'OTP');
    formData.append('host_login_id', sourceId);   
    formData.append('host_name', firstName);
    formData.append('host_midname', middleName);
    formData.append('host_lastname', lastName);
    formData.append('host_contact_number', number);
    formData.append('host_gst_no', gstin);
    formData.append('host_legal_name', companyName);
    formData.append('host_contact_email', email);
    formData.append('brand_name', brandName);

    try {
      const response = await axios({
        method: 'post',
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        url: `${params.baseURL}${params.endpoints.updateProfile}`,
        data: formData
      });

      if (response.data.status === 'updated') {
        setWithExpiry('hostProfile', JSON.stringify(response.data.profile_data));
        toast.success('Your profile data updated successfully.');
      } else if (response.data.status === 'error') {
        toast.error(response.data.list[0]);
      } else {
        toast.error('Error in saving data.');
      }
    } catch (err) {
      toast.error('An error occurred while saving data.');
    }
  } 

  const UpdateImage = async (imageDataUrl) => {
    const hostData = getItemFromLocalStorage('hostProfile');

    let formData = new FormData();
    formData.append('token', hostData.host_access_token);
    if (imageDataUrl) {
      const blob = base64ToBlob(imageDataUrl, 'image/jpg');
      formData.append('host_logo', blob);
    }
    formData.append('host_gst_no', gstin);
    formData.append('host_legal_name', companyName);
    try {
      const response = await axios({
        method: 'post',
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        url: `${params.baseURL}${params.endpoints.updateProfile}`,
        data: formData
      });

      if (response.data.status === 'updated') {
        setWithExpiry('hostProfile', JSON.stringify(response.data.profile_data));
        toast.success('Your profile photo updated successfully.');
      } else if (response.data.status === 'error') {
        toast.error(response.data.list);
      } else {
        toast.error('Error in saving data.');
      }
    } catch (err) {
      toast.error('An error occurred while saving data.');
    }
  } 

  return (
    <div>
      <Helmet>
          <title>My Profile</title>
      </Helmet>
      <section className="registration-form" style={{ height: '100%', overflow: 'hidden' }}>
      
        <div className='row'>
          <div className='col-md-6 register-left-panel'>
          </div>
          <div className='col-md-6 offset-md-6'>
            <div className="form-body">
              <div className="row">
                <div className="form-holder">
                  <div className="form-content">
                    <div className="form-items">
                      <h4 className='register-title mb-2'>Update Profile</h4>
                      <p className='mb-5'>Keep your profile fresh and up-to-date—because first impressions matter!</p>
                      <form className="needs-validation" encType="multipart/form-data" onSubmit={ProceedRegister}>

                        <div className='row'>
                          <div className="col-md-12 mb-4">
                            <div className='form-flex'>
                              <div className='pimg form-icon'>
                                <img 
                                  src='/assets/img/icons/pointer_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className="form-txt host-profile-section">
                                <label onClick={() => setShowCropper(true)}>
                                  <div>
                                    <img src={croppedImage || imagePreview}alt="Preview" className='host-profile-img' />
                                  </div>
                                </label>
                                {showCropper && (
                                  <ImageCropper
                                    onCrop={(croppedDataUrl) => {
                                      handleCrop(croppedDataUrl);
                                      UpdateImage(croppedDataUrl);
                                    }}
                                    onClose={() => setShowCropper(false)}
                                  />
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="col-md-12 mb-4">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img 
                                  src='/assets/img/icons/legal_name_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <input className={`form-control ${formErrors.firstName ? 'is-invalid' : ''}`} type="text" id="firstName" name="firstName" value={firstName} onChange={handleChange} onKeyUp={handleChange} onKeyDown={(e) => {
                                      if (e.key === " ") e.preventDefault();
                                  }} />
                                  <label htmlFor="firstName">First Name</label>
                                </div>
                                {/*<div className="txt-msg hcode-msg mb-0 mt-2" style={{background:'#e5e4e2',border:'1px solid #e5e4e2'}}>
                                  <p className="pid">Dear Host, your first name will be used as your preferred name until you add one yourself. The preferred name is what users see to know who their host is.</p>
                                </div>*/}
                              </div>
                            </div> 
                          </div>

                          <div className="col-md-12 mb-4">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img 
                                  src='/assets/img/icons/legal_name_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <input className={`form-control ${formErrors.middleName ? 'is-invalid' : ''}`} type="text" id="middleName" name="middleName" value={middleName} onChange={handleChange} onKeyUp={handleChange}/>
                                  <label htmlFor="middleName">Middle Name</label>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-12 mb-4">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img 
                                  src='/assets/img/icons/legal_name_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <input className={`form-control ${formErrors.lastName ? 'is-invalid' : ''}`} type="text" id="lastName" name="lastName" value={lastName} onChange={handleChange} onKeyUp={handleChange} />
                                  <label htmlFor="lastName">Last Name</label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className='row'>
                          <div className="col-md-12 mb-4">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img 
                                  src='/assets/img/icons/mail_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <input className={`form-control ${formErrors.email ? 'is-invalid' : ''}`} type="email" id="email" name="email" value={email} onChange={handleChange} onKeyUp={handleChange} />
                                  <label htmlFor="email">Email ID</label>
                                </div>
                              </div>
                            </div> 
                          </div>

                          <div className="col-md-12 mb-4">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img 
                                  src='/assets/img/icons/whatsapp_icon_r.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <input className={`form-control ${formErrors.number ? 'is-invalid' : ''}`} type="text" id="number" name="number" value={number} onChange={handleChange} onKeyUp={handleChange} />
                                  <label htmlFor="number">Number</label>
                                </div>
                              </div>
                            </div> 
                          </div>
                        </div>

                        <div className='row'>
                          <div className="col-md-12 mb-4">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img 
                                  src='/assets/img/icons/gst_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <input className="form-control" type="text" maxlength="15" id="gstin" name="gstin" value={gstin} onChange={handleGstinChange} />
                                  <label htmlFor="gstin">GSTIN (If Applicable)</label>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-12 mb-4">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img 
                                  src='/assets/img/icons/company_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <input className="form-control" type="text" id="companyName" name="companyName" value={companyName} onChange={handleChange} />
                                  <label htmlFor="companyName">Company Name</label>
                                </div>
                              </div>
                            </div>  
                          </div>

                          <div className="col-md-12 mb-4">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img 
                                  src='/assets/img/icons/company_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <input className="form-control" type="text" id="brandName" name="brandName" value={brandName} onChange={handleChange} />
                                  <label htmlFor="brandName">Brand Name</label>
                                </div>
                              </div>
                            </div>  
                          </div>

                        </div>

                        <div className='row'>
                        <div className="col-md-12 mb-3">
                          <div className='form-flex form-icon-box'>
                            <div className='form-icon'>
                              <img 
                                src='/assets/img/icons/register_icon.svg'
                                className='form-icon img-fluid'
                                alt=''
                              />
                            </div>
                            <div className='form-txt'>
                              <div className="form-button">
                                <button id="submit" type="submit" className="register-btn">Submit</button>
                              </div>
                            </div>
                          </div>  
                        </div>
                      </div>

                      <div className='row'>
                        <div className='col-md-12'>
                          <div className="form-check tc-box" style={{marginLeft:'55px'}}>
                            <label className="form-check-label"> 
                            I ACCEPT THE <a href={'https://wirestay.com/legal/hosting-agreement'} target='_blank' rel="noreferrer">TERMS & CONDITIONS</a> AND <a href={'https://wirestay.com/legal/privacy'} target='_blank' rel="noreferrer">PRIVACY POLICY</a> TO CONTINUE AS A WIRE STAY HOTEL PARTNER.
                            </label>
                          </div>
                        </div>
                      </div>
                      
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </section>
    </div>
  );
}

export default MyProfile;
